import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates sections when the DOM is ready. On the homepage only, the header's
 * optional featured products wait for menu intent. All navigation links remain
 * server-rendered and operable before this enhancement. Cart hydration is not
 * delayed. A failed enhancement can be retried at the next menu interaction.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    const section = document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
    const header = section?.querySelector('#header-component');
    const isHomepage = document.getElementById('MainContent')?.dataset.template === 'index';

    if (!header || !isHomepage || window.Shopify?.designMode) {
      requestIdleCallback(() => hydrateSection(sectionId, url));
      return;
    }

    const events = ['pointerover', 'pointerdown', 'focusin'];
    let loading = false;
    let complete = false;
    const enhance = () => {
      if (loading || complete) return;
      loading = true;
      hydrateSection(sectionId, url).then(() => {
        complete = true;
        for (const name of events) header.removeEventListener(name, onIntent, true);
      }).catch((error) => {
        console.warn('[Reparablo] Menu enhancement could not be loaded:', error);
      }).finally(() => { loading = false; });
    };
    const onIntent = (event) => {
      if (event.target instanceof Element && event.target.closest('header-menu, header-drawer')) enhance();
    };
    for (const name of events) header.addEventListener(name, onIntent, { capture: true, passive: true });
    // Keyboard focus or an already-open menu may precede this module's loading.
    if (header.querySelector('header-menu:focus-within, header-drawer:focus-within, header-drawer details[open], header-menu [aria-expanded="true"]')) {
      enhance();
    }
  });
}
